/**
 * Responsive layout management for the chat iframe.
 * Handles ResizeObserver, responsive CSS injection, dimension calculations,
 * and position helpers.
 * Extracted from IframeManager to keep the class focused on lifecycle.
 */
import type { OverlayLayout, WidgetState, WidgetDimensions, BackendOverlayConfig } from '../types';
import { MessageTypes } from '../types';
import {
  addEventListener,
  getViewportDimensions,
} from '../utils/domUtils';
import {
  getPositionStyles,
  getLauncherPositionStyles,
  injectCSS,
  FULL_VIEWPORT_HEIGHT,
} from '../utils/styleUtils';
import type { MessageBridge } from './MessageBridge';

/**
 * Height of the minimized header bar (px).
 * OverlayHeader renders at 52px (desktop) or 56px (mobile constrained viewport),
 * plus border (1px) and iframe body margin — totaling ~62px in the minimized strip.
 */
const MINIMIZED_HEADER_HEIGHT = 62;
/** Max width of the minimized bar (px). */
const MINIMIZED_BAR_WIDTH = 300;
/** Padding subtracted from viewport for constrained sizing (px). */
const VIEWPORT_PADDING = 40;

/**
 * Mutable state owned by IframeManager, passed by reference so handler
 * mutations are visible without manual write-back.
 */
export interface ResponsiveMutableState {
  resizeObserver: ResizeObserver | null;
}

export interface IframeResponsiveContext {
  iframe: HTMLIFrameElement | null;
  layout: OverlayLayout;
  messageBridge: MessageBridge | null;
  state: { loaded: boolean; communicating: boolean };
  debug: boolean;
  // Getter callbacks instead of scalar copies — avoids stale-snapshot bugs
  // when the context object is captured in a closure (e.g. ResizeObserver).
  getBackendOverlayConfig: () => BackendOverlayConfig | null;
  getActualBackendDimensions: () => { width: number; height: number } | undefined;
  getCurrentWidgetState: () => WidgetState;
  responsiveState: ResponsiveMutableState;
  eventCleanups: (() => void)[];

  // Callbacks into IframeManager
  resizeIframe: (dimensions: WidgetDimensions, state: WidgetState, animated: boolean) => void;
}

/**
 * Setup resize observer for responsive behavior
 */
export function setupResizeObserver(ctx: IframeResponsiveContext): void {
  if (!('ResizeObserver' in window)) {
    // Fallback to window resize event
    const resizeCleanup = addEventListener(window, 'resize', () => {
      handleResize(ctx);
    });
    ctx.eventCleanups.push(resizeCleanup);
    return;
  }

  ctx.responsiveState.resizeObserver = new ResizeObserver(() => {
    handleResize(ctx);
  });

  ctx.responsiveState.resizeObserver.observe(document.body);
}

/**
 * Handle resize event
 */
export function handleResize(ctx: IframeResponsiveContext): void {
  if (!ctx.iframe) return;

  // Recalculate dimensions for current widget state with new viewport size
  const dimensions = getResponsiveDimensions(ctx, ctx.getCurrentWidgetState());
  ctx.resizeIframe(dimensions, ctx.getCurrentWidgetState(), ctx.layout.animations || false);

  // Send resize event to iframe (only if loaded and communicating)
  if (ctx.messageBridge && ctx.state.loaded && ctx.state.communicating) {
    const viewport = getViewportDimensions();

    ctx.messageBridge.send(MessageTypes.PARENT_RESIZE, {
      width: viewport.width,
      height: viewport.height
    });
  }
}

/**
 * Get base position based on layout config
 */
function getBasePosition(ctx: IframeResponsiveContext): WidgetDimensions['position'] {
  // Spread backend partial layout over the full ctx.layout to satisfy OverlayLayout type
  const effectiveLayout: OverlayLayout = { ...ctx.layout, ...ctx.getBackendOverlayConfig()?.layout };
  const positionStyles = getPositionStyles(effectiveLayout);

  return {
    top: positionStyles.top || undefined,
    left: positionStyles.left || undefined,
    right: positionStyles.right || undefined,
    bottom: positionStyles.bottom || undefined
  };
}

function getLauncherBasePosition(ctx: IframeResponsiveContext): WidgetDimensions['position'] {
  const effectiveLayout: OverlayLayout = { ...ctx.layout, ...ctx.getBackendOverlayConfig()?.layout };
  const positionStyles = getLauncherPositionStyles(effectiveLayout);

  return {
    top: positionStyles.top || undefined,
    left: positionStyles.left || undefined,
    right: positionStyles.right || undefined,
    bottom: positionStyles.bottom || undefined
  };
}

/**
 * Get responsive dimensions based on widget state using backend config
 */
export function getResponsiveDimensions(ctx: IframeResponsiveContext, state: WidgetState): WidgetDimensions {
  const viewport = getViewportDimensions();

  // Use backend config dimensions if available, fallback to layout config
  const backendOverlayConfig = ctx.getBackendOverlayConfig();
  const configDimensions = backendOverlayConfig?.layout || ctx.layout;
  const { launcherSize, width, height, mobileFullScreen, launcherStyle } = configDimensions;

  if (ctx.debug) {
    console.log('[IframeManager] getResponsiveDimensions:', {
      state,
      hasBackendConfig: !!backendOverlayConfig,
      backendDimensions: backendOverlayConfig?.layout ? {
        width: backendOverlayConfig.layout.width,
        height: backendOverlayConfig.layout.height,
        launcherSize: backendOverlayConfig.layout.launcherSize
      } : null,
      usingDimensions: { width, height, launcherSize }
    });
  }

  // Always get base positioning from layout config
  const basePosition = getBasePosition(ctx);
  const launcherBasePosition = getLauncherBasePosition(ctx);

  // Helper to constrain dimensions to viewport
  const constrainToViewport = (dimension: number | string, maxAvailable: number, padding: number = 20): number | string => {
    if (typeof dimension === 'string') {
      if (dimension.includes('%') || dimension.includes('vw') || dimension.includes('vh')) {
        return dimension;
      }
      const numericDimension = parseInt(dimension, 10);
      const maxSize = maxAvailable - padding;
      if (numericDimension > maxSize) {
        return maxSize;
      }
      return numericDimension;
    }

    const maxSize = maxAvailable - padding;
    if (dimension > maxSize) {
      return maxSize;
    }
    return dimension;
  };

  // Handle different widget states using backend config dimensions
  switch (state) {
    case 'launcher': {
      const configuredLauncherSize = launcherSize || 60;
      const launcherBorderRadius = launcherStyle === 'square' ? 8 : 50;
      return {
        width: configuredLauncherSize,
        height: configuredLauncherSize,
        position: launcherBasePosition,
        borderRadius: launcherBorderRadius,
        zIndex: ctx.layout.zIndex || 9999
      };
    }

    case 'minimized': {
      const minimizedWidth = Math.min(MINIMIZED_BAR_WIDTH, viewport.width - VIEWPORT_PADDING);
      return {
        width: minimizedWidth,
        height: MINIMIZED_HEADER_HEIGHT,
        position: basePosition,
        borderRadius: ctx.layout.borderRadius || 0,
        zIndex: ctx.layout.zIndex || 9999
      };
    }

    case 'fullscreen':
      return {
        width: '100vw',
        height: FULL_VIEWPORT_HEIGHT,
        position: { top: 0, left: 0, right: 0, bottom: 0 },
        borderRadius: 0,
        zIndex: ctx.layout.zIndex || 9999
      };

    case 'expanded':
    default: {
      const configuredWidth = width || 400;
      const configuredHeight = height || 600;

      // Check if we should use mobile fullscreen
      const shouldUseFullscreen = mobileFullScreen &&
        (viewport.width < (typeof configuredWidth === 'number' ? configuredWidth : 400) + 40 ||
         viewport.height < (typeof configuredHeight === 'number' ? configuredHeight : 600) + 40);

      if (shouldUseFullscreen) {
        return {
          width: '100vw',
          height: FULL_VIEWPORT_HEIGHT,
          position: { top: 0, left: 0, right: 0, bottom: 0 },
          borderRadius: ctx.layout.borderRadius || 0,
          zIndex: ctx.layout.zIndex || 9999
        };
      }

      return {
        width: constrainToViewport(configuredWidth, viewport.width, backendOverlayConfig?.layout?.margin || 40),
        height: constrainToViewport(configuredHeight, viewport.height, backendOverlayConfig?.layout?.margin || 40),
        position: basePosition,
        borderRadius: ctx.layout.borderRadius || 0,
        zIndex: ctx.layout.zIndex || 9999
      };
    }
  }
}

/**
 * Get initial iframe styles (starts as launcher with default dimensions)
 */
export function getInitialIframeStyles(ctx: IframeResponsiveContext): Partial<CSSStyleDeclaration> {
  const launcherDimensions = getResponsiveDimensions(ctx, 'launcher');
  const positionStyles = getPositionStyles(ctx.layout);

  return {
    ...positionStyles,
    width: typeof launcherDimensions.width === 'number'
      ? `${launcherDimensions.width}px`
      : launcherDimensions.width,
    height: typeof launcherDimensions.height === 'number'
      ? `${launcherDimensions.height}px`
      : launcherDimensions.height,
    borderRadius: `${launcherDimensions.borderRadius || 50}px`,
    border: 'none',
    backgroundColor: 'transparent',
    display: 'block',
    opacity: '0',
    transition: ctx.layout.animations ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
    zIndex: (ctx.layout.zIndex || 9999).toString()
  };
}

/**
 * Inject responsive CSS styles dynamically based on layout config and widget state
 */
export function injectResponsiveStyles(
  ctx: IframeResponsiveContext,
  dimensions: { width: number; height: number },
  _currentState?: WidgetState,
): void {
  if (!ctx.iframe) return;

  const marginBuffer = ctx.layout.launcherOffset?.x || 20;

  // Breakpoints: config dimensions + margins
  const widthBreakpoint = dimensions.width + (marginBuffer * 2);
  const heightBreakpoint = dimensions.height + (marginBuffer * 2);

  // Get launcher size and minimized dimensions
  const launcherSize = ctx.layout.launcherSize || 60;
  // Minimized bar uses fixed width (not config width, which could be a parsed percentage)
  const viewport = getViewportDimensions();
  const minimizedWidth = Math.min(MINIMIZED_BAR_WIDTH, viewport.width - VIEWPORT_PADDING);
  const minimizedHeight = MINIMIZED_HEADER_HEIGHT;

  const css = `
      /* State-specific dimensions based on overlay config */

      /* Minimized state */
      .kaigentic-chat-iframe--minimized {
        width: ${minimizedWidth}px !important;
        height: ${minimizedHeight}px !important;
      }


      /* Responsive rules for launcher state */
      @media screen and (max-width: ${launcherSize + marginBuffer * 2}px) {
        .kaigentic-chat-iframe--launcher {
          width: 100vw !important;
          left: 0 !important;
          right: 0 !important;
        }
      }

      @media screen and (max-height: ${launcherSize + marginBuffer * 2}px) {
        .kaigentic-chat-iframe--launcher {
          height: 100vh !important;
          height: 100dvh !important;
          top: 0 !important;
          bottom: 0 !important;
        }
      }

      /* Responsive rules for minimized state */
      @media screen and (max-width: ${minimizedWidth + marginBuffer * 2}px) {
        .kaigentic-chat-iframe--minimized {
          width: 100vw !important;
          left: 0 !important;
          right: 0 !important;
        }
      }

      @media screen and (max-height: ${minimizedHeight + marginBuffer * 2}px) {
        .kaigentic-chat-iframe--minimized {
          height: 100vh !important;
          height: 100dvh !important;
          top: 0 !important;
          bottom: 0 !important;
        }
      }

      /* Responsive rules for expanded state */
      @media screen and (max-width: ${widthBreakpoint}px) {
        .kaigentic-chat-iframe--expanded {
          width: 100vw !important;
          left: 0 !important;
          right: 0 !important;
        }
      }

      @media screen and (max-height: ${heightBreakpoint}px) {
        .kaigentic-chat-iframe--expanded {
          height: 100vh !important;
          height: 100dvh !important;
          top: 0 !important;
          bottom: 0 !important;
        }
      }

      /* When both dimensions are constrained, only expanded state goes fullscreen.
         Launcher (button) and minimized (bar) keep their own dimensions. */
      @media screen and (max-width: ${widthBreakpoint}px) and (max-height: ${heightBreakpoint}px) {
        .kaigentic-chat-iframe--expanded {
          width: 100vw !important;
          height: 100vh !important;
          height: 100dvh !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }
      }
    `;

  injectCSS(css, 'kaigentic-responsive-styles');
}

/**
 * Calculate and apply responsive styles based on actual backend dimensions or layout config
 */
export function updateResponsiveStyles(ctx: IframeResponsiveContext, currentState?: WidgetState): void {
  if (!ctx.layout.responsive) return;

  const dimensions = ctx.getActualBackendDimensions() || {
    width: typeof ctx.layout.width === 'number' ? ctx.layout.width : parseInt(String(ctx.layout.width)) || 400,
    height: typeof ctx.layout.height === 'number' ? ctx.layout.height : parseInt(String(ctx.layout.height)) || 600
  };

  injectResponsiveStyles(ctx, dimensions, currentState);
}
